import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { contentColumnPadding } from '../../theme/constants';

interface Props {
  questions: string[];
  active: boolean;
  className?: string;
  /** When true, matches the hero textarea typography (15px). Otherwise 16px. */
  $hero?: boolean;
  /**
   * When true, holds the current question visible indefinitely and force-completes
   * any in-flight word fade-in so the full question is readable. Used by the
   * parent to let the user grab the currently-displayed suggestion (via hover or
   * a dedicated action button) without having it fade out mid-read.
   */
  paused?: boolean;
  /**
   * Fires whenever the currently-displayed question changes (including on mount).
   * Lets the parent wire an "Use this question" action to the exact string the
   * user is seeing right now.
   */
  onQuestionChange?: (question: string) => void;
}

const WORD_IN_MS = 80;
const WORD_STAGGER_MS = 45;
const HOLD_MS = 2500;
const FADE_OUT_MS = 400;

/**
 * Word-by-word fade-in rotating placeholder. Pauses when `active` is false
 * (i.e. textarea is focused or contains content). Respects
 * `prefers-reduced-motion` by falling back to a simple cross-fade.
 */
const AnimatedPlaceholder: React.FC<Props> = ({
  questions,
  active,
  className,
  $hero,
  paused = false,
  onQuestionChange,
}) => {
  const pool = useMemo(() => (questions.length ? questions : DEFAULT_POOL), [questions]);
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState<'in' | 'hold' | 'out'>('in');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);

  useEffect(() => {
    if (!active || paused) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }
    if (pool.length === 0) return;

    const words = pool[index % pool.length].split(/\s+/);
    const inDuration = WORD_IN_MS + words.length * WORD_STAGGER_MS + 200;

    if (phase === 'in') {
      timerRef.current = setTimeout(() => setPhase('hold'), inDuration);
    } else if (phase === 'hold') {
      timerRef.current = setTimeout(() => setPhase('out'), HOLD_MS);
    } else if (phase === 'out') {
      timerRef.current = setTimeout(() => {
        setIndex((prev) => (prev + 1) % pool.length);
        setPhase('in');
      }, FADE_OUT_MS);
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [phase, index, pool, active, paused]);

  // Reset cleanly when the pool changes
  useEffect(() => {
    setIndex(0);
    setPhase('in');
  }, [pool]);

  // When the parent pauses us mid-animation, snap phase forward to `hold`.
  // Two reasons:
  //   1. On resume, the words render as "settled" (opacity 1, no animation
  //      rule) instead of re-triggering the fade-in keyframe — no replay.
  //   2. The HOLD_MS countdown restarts from the unhover moment, so the same
  //      question lingers for the usual beat before advancing, giving the
  //      transition a natural feel instead of rushing to the next question.
  useEffect(() => {
    if (paused && phase !== 'hold') {
      setPhase('hold');
    }
  }, [paused, phase]);

  // Notify the parent which question is currently displayed so an external
  // "Use this question" action can target the exact string the user sees.
  // We fire on mount, on index change, and whenever the pool reshuffles.
  useEffect(() => {
    if (!active || pool.length === 0 || !onQuestionChange) return;
    onQuestionChange(pool[index % pool.length]);
  }, [index, pool, active, onQuestionChange]);

  if (!active || pool.length === 0) return null;

  const current = pool[index % pool.length];
  const words = current.split(/\s+/);

  // While paused we force the wrapper to opacity 1 (overrides any in-flight
  // fade-out) so the full question is readable regardless of animation phase.
  const fadingForStyle = paused ? false : phase === 'out';

  if (reducedMotion) {
    return (
      <Wrapper className={className} $fading={fadingForStyle} $hero={$hero}>
        <span>{current}</span>
      </Wrapper>
    );
  }

  // A word is "settled" once the question has passed its fade-in (phase is
  // past 'in'). Settled words render as plain opacity-1 with no animation,
  // which means flipping `$paused` false→true→false won't re-trigger the
  // fade-in keyframe when the user hovers and unhovers the same question.
  const settled = phase !== 'in';

  return (
    <Wrapper className={className} $fading={fadingForStyle} $hero={$hero}>
      {words.map((word, i) => (
        <Word
          key={`${index}-${i}`}
          $delayMs={i * WORD_STAGGER_MS}
          $settled={settled}
          $paused={paused}
        >
          {word}
          {i < words.length - 1 ? '\u00A0' : ''}
        </Word>
      ))}
    </Wrapper>
  );
};

const DEFAULT_POOL = [
  'What were my top-selling items this week?',
  'Show me revenue by location for the last 30 days.',
  'Which hours are the busiest across all locations?',
  'Compare this month versus last month.'
];

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Wrapper = styled.div<{ $fading: boolean; $hero?: boolean }>`
  pointer-events: none;
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-start;
  color: ${({ theme }) =>
    theme.mode === 'dark' ? theme.av2.textMuted : theme.av2.textLight};
  font-size: ${({ $hero }) => ($hero ? '15px' : '16px')};
  line-height: 1.55;
  font-family: inherit;
  white-space: pre-wrap;
  transition: opacity ${FADE_OUT_MS}ms ease;
  opacity: ${({ $fading }) => ($fading ? 0 : 1)};
  flex-wrap: wrap;
  user-select: none;
`;

const Word = styled.span<{ $delayMs: number; $settled: boolean; $paused: boolean }>`
  display: inline-block;
  opacity: 0;
  ${({ $settled, $paused, $delayMs }) =>
    $settled || $paused
      ? css`
          opacity: 1;
          animation: none;
        `
      : css`
          animation: ${fadeIn} ${WORD_IN_MS + 120}ms ease forwards;
          animation-delay: ${$delayMs}ms;
        `}
`;

export default AnimatedPlaceholder;
